"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { containerVariants, itemVariants } from "@/lib/utils/util";

export default function ManufacturingCapabilities() {
  const t = useTranslations('plsyy');

  const sections = [
    {
      title: t('strongRDTitle'),
      description: t('strongRDDesc'),
      image: "/image/plsyy/about/manufacturing-1.webp",
      layout: "text-left" // 左文右图
    },
    {
      title: t('advancedProductionTitle'),
      description: t('advancedProductionDesc'),
      image: "/image/plsyy/about/manufacturing-2.webp",
      layout: "text-right" // 左图右文
    },
    {
      title: t('accurateTestingTitle'),
      description: t('accurateTestingDesc'),
      image: "/image/plsyy/about/manufacturing-3.webp",
      layout: "text-left" // 左文右图
    },
    {
      title: t('globalTechnicalSupportTitle'),
      description: t('globalTechnicalSupportDesc'),
      image: "/image/plsyy/about/manufacturing-4.webp",
      layout: "text-right" // 左图右文
    }
  ];

  return (
    <div id="manufacturing-capabilities" className="bg-[#f8f9fa] py-16 max-md:py-10 scroll-mt-40">
      {/* Web端使用motion，手机端使用普通div */}
      <div className="container mx-auto max-md:block hidden">
        {/* 手机端：直接展示，无动画 */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-black leading-tight text-center">
            {t('manufacturingCapabilitiesTitle')}
          </h2>
        </div>

        {sections.map((section, idx) => {
          const isTextLeft = section.layout === "text-left";
          
          return (
            <div
              key={section.title}
              className={`grid grid-cols-1 gap-4 sm:gap-6 items-center border-b border-mainColor/30 py-6 sm:py-8 last:border-b-0 px-4 ${
                idx % 2 === 0 ? '' : 'flex-col-reverse'
              }`}
            >
              {/* 文字内容 */}
              <div className={isTextLeft ? "order-1" : "order-1"}>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {section.title}
                </h3>
                <div className="w-12 sm:w-16 h-1 bg-mainColor mb-3 sm:mb-4"></div>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  {section.description}
                </p>
              </div>

              {/* 图片卡片 */}
              <div className={`relative ${isTextLeft ? "order-2" : "order-2"}`}>
                <div className="bg-mainColor/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="relative h-[200px] sm:h-[250px] md:h-[300px] rounded-lg sm:rounded-xl overflow-hidden">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Web端：保持动画效果 */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
        className="container mx-auto max-md:hidden"
      >
        {/* 主标题 */}
        <motion.div
          variants={itemVariants}
          className="text-center mb-10 sm:mb-12 md:mb-16 px-4"
        >
          <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-black leading-tight text-center">
            {t('manufacturingCapabilitiesTitle')}
          </h2>
        </motion.div>

        {/* 交替布局的内容块 */}
        {sections.map((section, idx) => {
          const isTextLeft = section.layout === "text-left";
          
          return (
            <motion.div
              key={section.title}
              variants={itemVariants}
              className={`grid grid-cols-2 gap-5 items-center border-b border-mainColor/30 py-10 last:border-b-0 ${
                idx % 2 === 0 ? '' : ''
              }`}
            >
              {/* 文字内容 */}
              <motion.div 
                className={isTextLeft ? "order-1" : "order-2"}
                initial={{ opacity: 0, x: isTextLeft ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="text-xl sm:text-2xl xl:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                  {section.title}
                </h3>
                <div className="w-12 sm:w-16 h-1 bg-mainColor mb-4 sm:mb-6"></div>
                <p className="text-sm sm:text-base xl:text-lg leading-relaxed text-gray-700">
                  {section.description}
                </p>
              </motion.div>

              {/* 图片卡片 */}
              <motion.div 
                className={`relative ${isTextLeft ? "order-2" : "order-1"}`}
                initial={{ opacity: 0, x: isTextLeft ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="bg-mainColor/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative h-[300px] sm:h-[350px] md:h-[450px] rounded-lg sm:rounded-xl overflow-hidden">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

